import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import type { DepartmentOption, JobTitleOption } from '../types';

export interface RoleComboboxProps {
  id: string;
  /** The ACTIVE roles to choose from (the caller has already applied the department filter). */
  roles: JobTitleOption[];
  /** True when the company has any active role at all, so the empty state can say the right thing. */
  hasAnyRoles: boolean;
  departments: DepartmentOption[];
  /** The selected role id, or '' when none. */
  value: string;
  onChange: (role: JobTitleOption | null) => void;
  /** When given, a "Create new role" option is offered; it receives what was typed. */
  onCreateNew?: ((typed: string) => void) | undefined;
  disabled?: boolean | undefined;
  invalid?: boolean | undefined;
  describedBy?: string | undefined;
  required?: boolean | undefined;
  dataField?: string | undefined;
}

const CREATE_ID = '__create__';

/**
 * A type-ahead over the company's active job roles (WAI-ARIA 1.2 editable combobox with a list popup): the input is `role="combobox"`
 * with `aria-expanded`, `aria-controls` and `aria-activedescendant`; focus stays in the input while Up/Down move through the options,
 * Enter chooses, Escape closes the list (and never the dialog around it). Each option shows the role title and its department.
 * Typing after a choice clears the choice, so a role is only ever selected by choosing it. Choosing a role is a job-role choice only:
 * it never carries a permission.
 */
export function RoleCombobox({ id, roles, hasAnyRoles, departments, value, onChange, onCreateNew, disabled, invalid, describedBy, required, dataField }: RoleComboboxProps) {
  const uid = useId();
  const listId = `${uid}-list`;
  const selected = roles.find((r) => r.id === value) ?? null;
  const [text, setText] = useState(selected?.name ?? '');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  // Keep the box in step when the choice changes from outside (a role created in the small dialog, or cleared by a department change).
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setText(selected?.name ?? '');
  }, [selected?.id, selected?.name]);

  const deptName = useMemo(() => new Map(departments.map((d) => [d.id, d.name])), [departments]);
  const departmentOf = (r: JobTitleOption) => (r.departmentId ? (deptName.get(r.departmentId) ?? null) : null);

  const shown = useMemo(() => {
    const q = selected && text === selected.name ? '' : text.trim().toLowerCase();
    return roles
      .filter((r) => !q || [r.name, (r.departmentId ? deptName.get(r.departmentId) : '') ?? '', r.roleCode ?? ''].some((v) => v.toLowerCase().includes(q)))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [roles, text, selected, deptName]);

  const options: { id: string; role: JobTitleOption | null }[] = [
    ...shown.map((r) => ({ id: r.id, role: r })),
    ...(onCreateNew ? [{ id: CREATE_ID, role: null }] : []),
  ];
  const safeActive = Math.min(active, Math.max(0, options.length - 1));
  const optionDomId = (optionId: string) => `${uid}-opt-${optionId}`;

  function choose(optionId: string) {
    if (optionId === CREATE_ID) {
      setOpen(false);
      onCreateNew?.(text.trim() === selected?.name ? '' : text.trim());
      return;
    }
    const role = roles.find((r) => r.id === optionId);
    if (!role) return;
    setText(role.name);
    setOpen(false);
    onChange(role);
  }

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (disabled) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!open) { setOpen(true); setActive(0); } else setActive((safeActive + 1) % options.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (!open) { setOpen(true); setActive(Math.max(0, options.length - 1)); } else setActive((safeActive - 1 + options.length) % options.length);
    } else if (e.key === 'Home' && open) {
      e.preventDefault();
      setActive(0);
    } else if (e.key === 'End' && open) {
      e.preventDefault();
      setActive(Math.max(0, options.length - 1));
    } else if (e.key === 'Enter' && open) {
      // Never submit the form from inside the list.
      e.preventDefault();
      const option = options[safeActive];
      if (option) choose(option.id);
    } else if (e.key === 'Escape' && open) {
      // Closes the list only: the dialog around this field must stay open.
      e.preventDefault();
      e.stopPropagation();
      setOpen(false);
    } else if (e.key === 'Tab') {
      setOpen(false);
    }
  }

  const activeId = open && options[safeActive] ? optionDomId(options[safeActive]!.id) : undefined;

  return (
    <div className="rc">
      <input
        ref={inputRef}
        id={id}
        data-field={dataField}
        type="text"
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={activeId}
        aria-required={required ? true : undefined}
        aria-invalid={invalid ? true : undefined}
        aria-describedby={describedBy}
        autoComplete="off"
        spellCheck={false}
        placeholder="Search or select a role"
        value={text}
        disabled={disabled}
        onChange={(e) => {
          setText(e.target.value);
          setOpen(true);
          setActive(0);
          if (selected && e.target.value !== selected.name) onChange(null);
        }}
        onBlur={() => {
          setOpen(false);
          // Leave what was typed in place when nothing was chosen (validation will say so); restore the name when a role is chosen.
          if (selected) setText(selected.name);
        }}
        onKeyDown={onKeyDown}
        onClick={() => { if (!disabled) setOpen(true); }}
      />
      <span className="rc-chevron" aria-hidden="true">{'▾'}</span>
      <div className={open ? 'rc-popup' : 'rc-popup rc-popup--closed'} hidden={!open}>
        <ul id={listId} role="listbox" aria-label="Matching job roles" className="rc-list">
          {shown.map((r, i) => (
            <li
              key={r.id}
              id={optionDomId(r.id)}
              role="option"
              aria-selected={r.id === value}
              className={i === safeActive ? 'rc-option rc-option--active' : 'rc-option'}
              onMouseDown={(e) => e.preventDefault()}
              onMouseMove={() => setActive(i)}
              onClick={() => choose(r.id)}
            >
              <span className="rc-title">{r.name}</span>{' '}
              <span className="rc-dept">{departmentOf(r) ?? 'No department'}</span>
            </li>
          ))}
          {onCreateNew && (
            <li
              id={optionDomId(CREATE_ID)}
              role="option"
              aria-selected={false}
              className={safeActive === shown.length ? 'rc-option rc-option--create rc-option--active' : 'rc-option rc-option--create'}
              onMouseDown={(e) => e.preventDefault()}
              onMouseMove={() => setActive(shown.length)}
              onClick={() => choose(CREATE_ID)}
            >
              <span aria-hidden="true">+ </span>Create new role
            </li>
          )}
        </ul>
        {open && shown.length === 0 && (
          <p className="rc-empty" role="status">
            {hasAnyRoles
              ? 'No active roles match what you typed for this department.'
              : 'No active roles yet. Create a new role below, or ask a company administrator to add one under People, Roles.'}
          </p>
        )}
        {open && (
          <span className="rc-sr-only" role="status">
            {shown.length === 1 ? '1 role available' : `${shown.length} roles available`}
          </span>
        )}
      </div>
    </div>
  );
}
